import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";

// Register GSAP ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

export default function Experience() {
  const cardRefs = useRef([]);

  useEffect(() => {
    cardRefs.current.forEach((card, index) => {
      // For cards that should come from the left
      if (index % 2 === 0) {
        gsap.fromTo(
          card,
          { x: -200, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 1.5,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 80%",
              end: "center center",
              scrub: 1,
              toggleActions: "play none none reverse",
            },
          }
        );
      } else {
        // For cards that should come from the right
        gsap.fromTo(
          card,
          { x: 200, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 1.5,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 80%",
              end: "center center",
              scrub: 1,
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    });

    // Cleanup on unmount
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div className="relative flex flex-col items-center gap-5 sm:gap-10 sm:flex-row md:items-center md:justify-between bg-white px-6 lg:px-20 md:py-16 py-6 overflow-hidden">
      {/* Geometric Shapes Background */}
      <div className="absolute top-0 left-0 w-48 h-48">
        <div className="absolute top-8 left-8 w-24 h-24 bg-indigo-200 rounded-full opacity-30"></div>
        <div className="absolute top-4 left-4 w-16 h-16 bg-indigo-300 rounded-full opacity-30"></div>
        <div className="absolute top-12 left-2 w-12 h-12 bg-indigo-400 rounded-full opacity-30"></div>
      </div>
      <div className="absolute bottom-0 right-0 w-48 h-48">
        <div className="absolute bottom-8 right-8 w-24 h-24 bg-indigo-200 transform rotate-45 opacity-30"></div>
        <div className="absolute bottom-4 right-4 w-16 h-16 bg-indigo-300 transform rotate-45 opacity-30"></div>
      </div>

      <div className="grid grid-cols-1 lg:px-20 w-full md:w-[60%] relative z-10">
        {/* Projects Completed */}
        <div
          ref={(el) => (cardRefs.current[0] = el)}
          className="flex flex-col md:ms-28 ms-16 items-start px-10 py-5 text-start shadow-lg relative"
        >
          <img
            src="./assets/Vector.png"
            alt=""
            className="absolute md:w-20 md:h-20 w-14 h-14 -right-5 -top-5"
          />
          <p className="md:text-4xl text-2xl font-bold text-gray-800 mb-2">125</p>
          <div className="flex gap-2">
            <p className="h-5 w-1 bg-orange-500"></p>
            <p className="text-gray-500">Projects Completed</p>
          </div>
        </div>

        {/* Happy Clients */}
        <div
          ref={(el) => (cardRefs.current[1] = el)}
          className="flex flex-col md:me-28 me-16 items-start px-10 py-5 text-start shadow-lg relative"
        >
          <img
            src="./assets/Vector1.png"
            alt=""
            className="absolute md:w-20 md:h-20 w-14 h-14 -right-5 -top-5"
          />
          <p className="md:text-4xl text-2xl font-bold text-gray-800 mb-2">225</p>
          <div className="flex gap-2">
            <p className="h-5 w-1 bg-orange-500"></p>
            <p className="text-gray-500">Happy Clients</p>
          </div>
        </div>

        {/* Awards */}
        <div
          ref={(el) => (cardRefs.current[2] = el)}
          className="flex flex-col md:ms-28 ms-14 items-start px-10 py-5 text-start shadow-lg relative"
        >
          <img
            src="./assets/Vector2.png"
            alt=""
            className="absolute md:w-20 md:h-20 w-14 h-14 -right-5 -top-5"
          />
          <p className="md:text-4xl text-2xl font-bold text-gray-800 mb-2">7</p>
          <div className="flex gap-2">
            <p className="h-5 w-1 bg-orange-500"></p>
            <p className="text-gray-500">Awards Won</p>
          </div>
        </div>

        {/* Years in Business */}
        <div
          ref={(el) => (cardRefs.current[3] = el)}
          className="flex flex-col md:me-28 me-14 items-start px-10 py-5 text-start shadow-lg relative"
        >
          <img
            src="./assets/Vector3.png"
            alt=""
            className="absolute md:w-20 md:h-20 w-14 h-14 -right-5 -top-5"
          />
          <p className="md:text-4xl text-2xl font-bold text-gray-800 mb-2">10</p>
          <div className="flex gap-2">
            <p className="h-5 w-1 bg-orange-500"></p>
            <p className="text-gray-500">Years in Business</p>
          </div>
        </div>
      </div>

      {/* Experience Description */}
      <div className="mt-8 md:mt-0 md:ml-12 flex flex-col items-start md:items-start md:w-[40%] relative z-10">
        <h2 className="md:text-4xl text-2xl font-bold text-indigo-900 md:text-left">
          DECADE OF EXPERIENCE
        </h2>
        <p className="text-[#152868] mt-4 text-start md:text-left">
          With strong self-confidence and a team of highly qualified professionals, Northern Professional Kitchen Equipment has rapidly advanced to become a specialist in the import, supply, and installation of industrial kitchen and catering equipment for its valued clients.
        </p>
        <Link to={'/contactus'} className="mt-6 bg-indigo-900 text-white px-6 py-3 shadow hover:bg-blue-950">
          Contact Us
        </Link>
      </div>
    </div>
  );
}